/**
 * Responsive grid of filtered and sorted product cards.
 *
 * @module views/components/ProductListingWrapper
 * @memberof -Common
 */
import './ProductListingWrapper.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import merge from 'deepmerge';
import { Formik } from 'formik';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import GhostCards from '@ulta/core/components/GhostCards/GhostCards';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { hasItems, removeTargetDuplicates } from '@ulta/core/utils/array/array';
import { DXL_NAVIGATION_TYPE } from '@ulta/core/utils/constants/action';
import { triggerScrollDCEvent } from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { handleRedirect, handleReload, handleReplace } from '@ulta/core/utils/handleLocation/handleLocation';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';
import { getStorage, removeStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import FilterableContentProvider, {
  useFilterableContentContext
} from '@ulta/providers/FilterableContentProvider/FilterableContentProvider';

import FilterResultsAction from '@ulta/modules/FilterResultsAction/FilterResultsAction';
import FiltersColumn from '@ulta/modules/FiltersColumn/FiltersColumn';
import GamBanner from '@ulta/modules/GamBanner/GamBanner';

import { constants } from '@ulta/utils/constants/constants';
import { FILTER_EVENT, FILTER_GROUPS } from '@ulta/utils/constants/filters';
import interpolateString from '@ulta/utils/interpolateString/interpolateString';
import { isSafari } from '@ulta/utils/isSafari/isSafari';
import { listenForElement } from '@ulta/utils/listenForElement/listenForElement';

import BlockMessage from '../BlockMessage/BlockMessage';
import GuidedNavigation from '../GuidedNavigation/GuidedNavigation';
import LoadContent from '../LoadContent/LoadContent';
import * as utils from './ProductListingWrapper';

/**
 * Represents a ProductListingWrapper component
 *
 * This component is meant to be used as an abstraction to filterable content provider
 *
 * Currently used for ProductListingWrapper and PromotionListingResults
 *
 * @method
 * @param {ProductListingWrapperProps} props - React properties passed from composition
 * @returns ProductListingWrapper
 */
export const ProductListingWrapper = function( props ){
  const { filtersEnabled:propsFiltersEnabled = defaultProps.filtersEnabled } = props;
  const { invokeAction, currentUrl, meta } = props;
  const { breakpoint } = useDeviceInflection();
  const isMobileDevice = breakpoint.CURRENT_BREAKPOINT === 'SM';
  const filtersEnabled = propsFiltersEnabled !== false && !!props.filtersColumn;
  const showGambanner = breakpoint.CURRENT_BREAKPOINT === 'XL' || breakpoint.CURRENT_BREAKPOINT === 'SM';
  const isInitialRequest = useRef( true );
  const loading = utils.resolveLoadingState( { meta, loading: props.loading } );

  return (
    <FilterableContentProvider
      loading={ loading }
      invokeAction={ invokeAction }
      environment={ isMobileDevice ? 'm' : 'd' }
      currentUrl={ currentUrl }
      responseModuleParams={ props }
      isInitialRequest={ isInitialRequest }
    >
      <ProductListingInner
        { ...spreadProps( { ...props } ) }
        isMobileDevice={ isMobileDevice }
        filtersEnabled={ filtersEnabled }
        showGambanner={ showGambanner }
        isInitialRequest={ isInitialRequest }
        loading={ loading }
      />
    </FilterableContentProvider>
  );
};
export const RESOURCE_TYPE = { Default: 'default', Favorites: 'favorites' };

export const ProductListingInner = function( props ){
  const {
    children,
    componentKey,
    filtersColumn,
    filtersEnabled = ProductListingInner.defaultProps.filtersEnabled,
    filtersResultAction,
    filterByStorePill,
    isMobileDevice,
    items,
    itemSelectorTemplate,
    loading,
    loadMoreAction,
    loadPreviousAction,
    loadMoreUrl,
    loadPreviousUrl,
    noFilteredResultsLabel,
    noResultsLabel,
    skipAutoCorrectAction,
    skipRedirectAction,
    skipRedirectLabel,
    pageTitle,
    resetFiltersAction,
    resultCountLabel,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    search,
    showGambanner,
    gamBanner: gamBannerModel,
    guidedNavigation: guidedNavigationModel,
    invokeAction,
    threshold = defaultProps.threshold,
    pageSize,
    loadMoreTemplateUrl,
    page,
    productsViewedTemplateLabel,
    dynamicAds,
    interrupterRows,
    itemSelectorClassName,
    itemSelectorAttribute,
    resultCount,
    itemIndexAttribute,
    resourceType,
    messages,
    meta
  } = props;

  const { previewDate, redirectedSearchTerm, isRedirect } = useAppConfigContext();

  const headerStyle =
    resourceType === RESOURCE_TYPE.Favorites ?
      {
        className: 'ProductListingWrapper__resultslabel__adjusted-for-wrapper',
        textStyle: 'eyebrow',
        color: 'neutral-600'
      } :
      { textStyle: 'title-6' };

  const htmlTag = resourceType === RESOURCE_TYPE.Favorites ? 'p' : 'h2';

  const [productsList, setProductsList] = useState( items );
  const [dynamicAdsList, setDynamicAdsList] = useState( dynamicAds );
  const [interrupterRowsList, setInterrupterRowsList] = useState( interrupterRows );

  const [showGhostCards, setShowGhostCards] = useState( !items );

  /*
  * Watch for Loader to set shouldShowLoader to false to hide default loader.
  */
  useEffect( () => {
    utils.manageLoadingState( { loading }, { setShowGhostCards } );
  }, [loading] );

  const [isFilterShown, setIsFilterShown] = useState( false );
  const { setFilterRequest, isSearch, previousAction } = useFilterableContentContext();
  const [loader] = useLoader( { loading, color: 'orange-400' } );
  const { openOverlay, closeOverlay } = useOverlay();
  const showFiltersButtonRef = useRef( null );
  const [productsViewedCountLabel, setProductsViewedCountLabel] = useState();
  const gamBanner = useRef( showGambanner ? <GamBanner { ...spreadProps( { ...gamBannerModel } ) } /> : null );

  /*
  * Restrict filtersColumn to render if we are getting filterColumn as content in overlay
  */
  const isFiltersColumn = !isFilterShown && filtersEnabled;

  const isFilterDisplayedOnMobile = isFilterShownOnMobile( {
    isMobileDevice,
    isFilterShown
  } );

  const isNoResultsLabelShown = isEmptyResultsLabelShown( {
    productsList,
    isMobileDevice,
    isFilterShown
  } );

  const isLoadPreviousShown = useMemo(
    () => utils.isLoadPreviousShown( { productsList, pageSize, loadPreviousAction } ),
    [productsList, pageSize, loadPreviousAction]
  );

  const isLoadMoreShown = useMemo(
    () => utils.isLoadMoreShown( { productsList, pageSize, resultCount, loadMoreAction } ),
    [productsList, resultCount, loadMoreAction]
  );

  // Filter the items where selected is false on Guided Navigation. Used to not render the GuidedNavigation items if all the items in the array are false
  const guidedNavItems = guidedNavigationModel?.items?.filter( item => !item.selected ) || [];

  const appliedFiltersAndGuidedNavigation = useCallback(
    utils.composeAppliedFiltersAndGuidedNavigation(
      { guidedNavigationModel, filtersColumn }
    ),
    [guidedNavigationModel, filtersColumn?.appliedFilters]
  );

  const isGuidedNavVisible = guidedNavigationModel?.items?.length > 0 &&
  guidedNavItems?.length !== 0;

  const filtersColumnProps = {
    ...filtersColumn,
    componentKey,
    isFilterShown,
    isMobileDevice,
    resultCountLabel,
    resultCount,
    noResultsLabel,
    setFilterRequest,
    loading,
    showFiltersButtonRef,
    search,
    gamBanner,
    setIsFilterShown,
    closeOverlay,
    filterByStorePill
  };

  const searchResultsHtmlTag = isSearch ? 'h1' : htmlTag;

  /*
   * Filter user preferences / state
   */
  const previouslyScrolledSku = useRef();
  const isScrolling = useRef();

  /*
   * On card intersection callback
   */
  const debounceIntersectionRef = useRef();
  const debouncedIntersectionHandler = useCallback( () => {
    const firstVisibleElement = document.querySelector( '.ProductListingWrapper__productList [data-visible="true"]' );
    const id = firstVisibleElement?.getAttribute( 'data-item-id' );
    if( !id || !itemsDict.current?.[id]?.pageUrl ){
      return;
    }
    const pageUrl = itemsDict.current[id].pageUrl;
    pageUrl && handleReplace( { url: pageUrl, previewDate, preserveParams: true } );
  } );
  const onIntersectionEvent = useCallback(
    utils.composeOnCardIntersect(
      { itemSelectorTemplate, isScrolling, debounceIntersectionRef, loading },
      { debouncedIntersectionHandler }
    ),
    [loading]
  );

  /*
   * Datacapture for module intersection and onCardIntersect
   */
  const productListingRef = useRef();
  const itemsDict = useRef( [] );
  const visibleItems = useRef( [] );
  const dcTimer = useRef();
  const eventTriggeredItemList = useRef( [] );

  useIntersectionObserver(
    productListingRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  /*
   * On card data capture callback
   */
  const onDataCaptureEvent = useCallback( utils.composeOnCardDataCapture( { visibleItems, loading } ), [loading] );

  /*
   * useEffect Hook that updates the itemsDict with the list of items, based on the navigation type it triggers the loadpreviousSku method.
   * updates the products viewed label, and updates the visibility of the load previous button.
   */
  useEffect( () => {
    itemsDict.current = utils.getItemList( { productsList, pageSize, loadMoreTemplateUrl, itemIndexAttribute } );

    utils.updateProductsViewedLabel(
      { productsList, productsViewedTemplateLabel, pageSize },
      { setProductsViewedCountLabel }
    );
  }, [productsList] );

  /*
   * On mount useEffect:
   * 1. Apply user's filter preference
   * 2. Setup Datacapture interval that will dispatch queued card intersection events
   * 3. `loadFilterState` Get/set filter open/closed
   * 4. `loadPreviousSku` get previously scrolled sku -> execute scroll
   * 5. adds product click event listner to store the previously scrolled product key
   * 6. Handle safari edge cases around navigation
   */
  useEffect( () => {
    utils.loadFilterState( { setIsFilterShown } );
    utils.loadPreviousSku( { previouslyScrolledSku } );

    global.clearInterval( dcTimer.current );

    dcTimer.current = utils.setupDCTimer( { visibleItems, itemsDict, eventTriggeredItemList } );

    productListingRef.current?.addEventListener( 'click', ( event ) => {
      utils.handleProductClick(
        {
          event,
          itemSelectorClassName,
          itemSelectorAttribute,
          itemsDict,
          PREVIOUSLY_SCROLLED_STORAGE_KEY,
          previewDate
        },
        { handleReplace, setStorage }
      );
    } );

    /*
     * Handles Safari specific navigation edge cases
     */
    global.addEventListener( 'pageshow', utils.safariReload );

    return () => {
      // Removes safari listener
      global.removeEventListener( 'pageshow', utils.safariReload );

      // Clears interval for batch/queue DC processor
      global.clearInterval( dcTimer.current );
    };
  }, [] );

  /*
   * Loading state changes useEffect
   */
  useEffect( () => {
    /*
     * Watch for Loader to set shouldShowLoader to false to hide default loader.
     */
    utils.manageLoadingState( { loading }, { setShowGhostCards } );

    /*
     * scroll to the previously scrolled product
     */
    utils.scrollToPreviousSku( {
      loading,
      items,
      previouslyScrolledSku,
      productListingRef,
      itemSelectorTemplate,
      isScrolling,
      meta
    } );
  }, [loading] );

  /*
   * Update local storage when user filter preferences have been updated.
   */
  useEffect(
    () => utils.onShowFiltersChange( { isFilterShown, isMobileDevice }, { closeOverlay } ),
    [isFilterShown, isMobileDevice, closeOverlay]
  );

  /*
   * Listens for screen size changes and closes the overlay if needed
   */
  useEffect( () => {
    utils.closeFiltersOverlayOnDesktop( { isMobileDevice }, { closeOverlay } );
  }, [isMobileDevice] );

  /*
   * Prepares column for overlay
   */
  const injectFilterColumnsIntoOverlay = useMemo(
    () =>
      !isFiltersColumn ? (
        <Formik initialValues={ {} }>
          <FiltersColumn
            { ...spreadProps( { ...filtersColumnProps } ) }
            isOverlayOpen={ true }
            showAppliedFilters={ false }
            sortOptions={ filtersResultAction?.sortItems }
            sortLabel={ filtersResultAction?.sortLabel }
          />
        </Formik>
      ) : null,
    [filtersColumnProps]
  );

  /*
   * Watch for changes to filtersColumn and open overlay
   */
  useEffect( () => {
    openFiltersOverlay(
      {
        filtersEnabled,
        isMobileDevice,
        isFilterShown,
        opener: showFiltersButtonRef,
        content: injectFilterColumnsIntoOverlay,
        closeAction: filtersColumn?.doneAction
      },
      {
        openOverlay,
        setIsFilterShown
      }
    );
  }, [isMobileDevice, isFilterShown, filtersColumn, loading] );

  /*
   * Manages merging items during load more operations
   */
  useEffect( () => {
    if( !Array.isArray( items ) ){
      return;
    }

    utils.updateProductLists( { items, previousAction, page, guidedNavigationModel }, { setProductsList } );
  }, [items] );

  /*
   * Manages merging dynamicAds and interrupterRows during load more operations
   */
  useEffect( () => {
    utils.updateInterrupters(
      { props, interrupterRowsList, dynamicAdsList, previousAction, guidedNavigationModel, appliedFilters: filtersColumn?.appliedFilters },
      { setDynamicAdsList, setInterrupterRowsList } );
  }, [dynamicAds, interrupterRows] );

  // Set Gambanner ref for XL and SM devices
  useEffect( () => {
    gamBanner.current = null;
    if( showGambanner && gamBannerModel ){
      gamBanner.current = <GamBanner { ...spreadProps( { ...gamBannerModel } ) } />;
    }
  }, [showGambanner, gamBannerModel] );

  const renderChildren = useCallback(
    composeChildRenderer( {
      interrupterAds:  dynamicAdsList,
      interrupterRowAds: interrupterRowsList,
      items: productsList,
      isFilterShown
    } ),
    [dynamicAdsList, interrupterRowsList, productsList, isFilterShown]
  );
  /*
   * Props that will be passed to all children
   */
  const childrenRenderProps = {
    interrupterAds: dynamicAdsList,
    interrupterRowAds: interrupterRowsList,
    items: productsList,
    isFilterShown,
    loading,
    onDataCaptureEvent,
    onIntersectionEvent,
    renderChildren
  };

  const messagesAvailable = hasItems( messages );

  const showSearchResultsOnMobile = resultCountLabel && ( search || isMobileDevice );
  const showFiltersResultsAction = !isFilterDisplayedOnMobile && filtersResultAction?.sortItems;

  return (
    <div
      className={ classNames( `ProductListingWrapper ProductListingWrapper--wrapper`, {
        [props.className]: props.className,
        ProductListingWrapper__isSearchResult: search || isMobileDevice
      } ) }
      data-test='product-listing-wrapper'
      id='product-listing-wrapper'
      ref={ productListingRef }
    >
      <Formik initialValues={ {} }>
        <>
          <GridContainer>
            <div
              className={ classNames( 'ProductListingWrapper__filtersControls', {
                'ProductListingWrapper__filtersColumn__isSearchResult': search || isMobileDevice
              } ) }

              data-test='filters-column-wrapper'
            >
              <FiltersColumn
                { ...spreadProps( { ...filtersColumnProps, invokeAction } ) }
              />
            </div>
            { isGuidedNavVisible && (
              <div className='ProductListingWrapper__guidedNavigation'>
                <GuidedNavigation { ...spreadProps( { ...guidedNavigationModel, invokeAction } ) } />
              </div>
            ) }
            <div className='ProductListingWrapper__appliedFilters'>
              <FiltersColumn
                { ...spreadProps( { ...filtersColumnProps } ) }
                appliedFiltersAndGuidedNavigation={ appliedFiltersAndGuidedNavigation() }
                isOverlayOpen={ false }
                showAppliedFilters={ true }
                sortOptions={ filtersResultAction?.sortItems }
                sortLabel={ filtersResultAction?.sortLabel }
              />
            </div>

            { resultCountLabel && !search && !isMobileDevice && (
              <div className={ classNames( 'ProductListingWrapper__searchResultsHeader', {
                'ProductListingWrapper__searchResultsHeader--withGuidedNavigation': !!guidedNavigationModel || filtersColumn?.appliedFilters?.length
              } ) }
              >
                <SearchResultsHeader
                  resultCountLabel={ resultCountLabel }
                  noResultsLabel={ noResultsLabel }
                  skipAutoCorrectAction={ skipAutoCorrectAction }
                  skipRedirectAction={ skipRedirectAction }
                  skipRedirectLabel={ skipRedirectLabel }
                  isRedirect={ isRedirect }
                  redirectedSearchTerm={ redirectedSearchTerm }
                  pageTitle={ pageTitle }
                  ariaBusy={ loading }
                  htmlTag={ searchResultsHtmlTag }
                  search={ search }
                  isMobileDevice={ isMobileDevice }
                  headerStyle={ headerStyle }
                />
                { !filtersResultAction && <div className='ProductListingWrapper__hiddenDiv' /> }
                { showFiltersResultsAction && (
                  <div className='ProductListingWrapper__filterResultsAction'
                    data-test='results-action-wrapper'
                  >
                    <FilterResultsAction
                      sortItems={ filtersResultAction?.sortItems }
                      sortLabel={ filtersResultAction?.sortLabel }
                      onChange={ ( data ) => onSort( { data }, { setFilterRequest } ) }
                    />
                  </div>
                ) }
              </div>
            ) }

            { showSearchResultsOnMobile &&
              <div className='ProductListingWrapper__searchResultsHeader'>
                <SearchResultsHeader
                  resultCountLabel={ resultCountLabel }
                  noResultsLabel={ noResultsLabel }
                  skipAutoCorrectAction={ skipAutoCorrectAction }
                  skipRedirectAction={ skipRedirectAction }
                  skipRedirectLabel={ skipRedirectLabel }
                  isRedirect={ isRedirect }
                  redirectedSearchTerm={ redirectedSearchTerm }
                  pageTitle={ pageTitle }
                  ariaBusy={ loading }
                  htmlTag={ searchResultsHtmlTag }
                  search={ search }
                  isMobileDevice={ isMobileDevice }
                  headerStyle={ headerStyle }
                />
                { !filtersResultAction && <div className='ProductListingWrapper__hiddenDiv' /> }
              </div>
            }
          </GridContainer>
          <GridContainer>
            { messagesAvailable &&
              messages.map( ( data, index ) => (
                <div className='ProductListingWrapper__blockErrorMessage'
                  key={ `${index}-${componentKey}` }
                >
                  <BlockMessage
                    message={ data.message }
                    messageType={ data.icon }
                    backgroundColor={ data.backgroundColor }
                    borderColor={ data.borderColor }
                    textColor={ data.textColor }
                    dismissAction={ data.dismissAction }
                    action1={ data.action1 }
                    action1Style='link'
                    action2={ data.action2 }
                    ariaLiveType='assertive'
                    role='alert'
                  />
                </div>
              ) ) }

            { isNoResultsLabelShown && (
              <>
                <div className='ProductListingWrapper__emptyResultsMessage'
                  data-test='empty-results-markdown'
                >
                  <Markdown cmsMarkdownContent={ noFilteredResultsLabel } />
                </div>

                { isMobileDevice &&
                  // we are using overlay now so we don't require this section
                  // filterParams contain applied category filters in a response, but appliedFilters property - does not
                  filtersColumn?.filterParams?.length > 0 && (
                  <div className='ProductListingWrapper__mobileResetButton'
                    data-test='mobile-reset-button'
                  >
                    <Button label={ resetFiltersAction?.label }
                      secondary={ true }
                      compact={ true }
                      alignment={ 'center' }
                    />
                  </div>
                ) }
              </>
            ) }

            { !isFilterDisplayedOnMobile && !isNoResultsLabelShown && (
              <div className='ProductListingWrapper__productList'>
                { loading && <>{ loader }</> }
                { !loading && (
                  <div className='ProductListingWrapper__LoadContent__previous'
                    data-test='load-previous-wrapper'
                  >
                    { isLoadPreviousShown && (
                      <LoadContent
                        label={ loadPreviousAction?.label }
                        loadContentAction={ loadPreviousAction }
                        loadContentUrl={ loadPreviousUrl }
                        onLoadContentClick={ () => {
                          onLoadClick(
                            {
                              loadAction: loadPreviousAction,
                              id: `loadPrevious-${new Date().getTime()}`,
                              event: FILTER_EVENT.LOAD_PREVIOUS
                            },
                            { setFilterRequest, setShowGhostCards }
                          );
                        } }
                      />
                    ) }
                  </div>
                ) }
                { children && children( childrenRenderProps ) }
                { !loading && (
                  <div className='ProductListingWrapper__LoadContent'
                    data-test='load-more-wrapper'
                  >
                    { isLoadMoreShown ? (
                      <LoadContent
                        label={ loadMoreAction?.label }
                        loadContentTitle={ productsViewedCountLabel }
                        loadContentAction={ loadMoreAction }
                        loadContentUrl={ loadMoreUrl }
                        onLoadContentClick={ () =>
                          onLoadClick(
                            {
                              loadAction: loadMoreAction,
                              id: `loadMore-${new Date().getTime()}`,
                              event: FILTER_EVENT.LOAD_MORE
                            },
                            { setFilterRequest, setShowGhostCards }
                          )
                        }
                      />
                    ) : (
                      <Text textStyle={ 'body-2' }
                        textAlign={ 'center' }
                      >
                        { productsViewedCountLabel }
                      </Text>
                    ) }
                  </div>
                ) }
              </div>
            ) }
            { showGhostCards && (
              <div className='ProductListingWrapper__ghostCards'
                data-test='ghost-cards-wrapper'
                aria-hidden='true'
              >
                <GhostCards isFilterShown={ isFilterShown } />
              </div>
            ) }
          </GridContainer>
        </>
      </Formik>
    </div>
  );
};

ProductListingInner.defaultProps = {
  filtersEnabled: true
};

/**
 * Determines loading state based on if we have a follow up call or not
 * If we have an init action we should declare loading ASAP before the call is dispatched officially
 *
 * @param {object} data - arguments
 * @param {object} data.meta - meta object
 * @param {boolean} data.loading - loading from layerhost
 *
 * @returns {boolean}
 */
export const resolveLoadingState = ( data ) => {
  const { loading, meta = {} } = handleEmptyObjects( data );
  return !!meta.initAction || loading;
};

/**
 * To render product cards with ads if available
 */
export const composeChildRenderer = ( data ) => ( childNodeRenderer ) => {
  const { inflection, breakpoint } = useDeviceInflection();
  const { items, interrupterAds, interrupterRowAds } = data || {};
  const combinedArray = items?.map( ( module, index ) => {
    return childNodeRenderer( module, index );
  } );

  if( !interrupterAds && !interrupterRowAds ){
    return combinedArray;
  }

  if( interrupterAds ){
    const className = '__interrupterAds';
    const filteredInterrupterAds = utils.getFilteredAds( { adsModule: interrupterAds, inflection } );
    filteredInterrupterAds &&
      utils.getAdsRendered( { filteredAds: filteredInterrupterAds, combinedArray, inflection, className } );
  }

  if( interrupterRowAds ){
    const className = '__interrupterRowAds';
    const filteredInterrupterRowAds = utils.getFilteredAds( { adsModule: interrupterRowAds, inflection } );
    const productPerRow = utils.getProductPerRow( { breakpoint } );
    const newFilteredInterrupterRowAds = filteredInterrupterRowAds?.sort( ( a, b )=>a?.position - b?.position ).map( ( module, index ) => {
      const position = productPerRow * ( module?.position - 1 ) + ( index + 1 );
      return { ...module, position };
    } );
    newFilteredInterrupterRowAds &&
      utils.getAdsRendered( { filteredAds: newFilteredInterrupterRowAds, combinedArray, inflection, className } );
  }
  return combinedArray;
};

/**
 * To glue together the selected applied fields and the selected guided navigation
 *  feilds into one array so we can display as dismissible pills
 */
export const composeAppliedFiltersAndGuidedNavigation = ( data ) => () => {
  const { guidedNavigationModel, filtersColumn } = handleEmptyObjects( data );

  if( !filtersColumn ){
    return;
  }

  // Filter out stores from applied filters as the StoreFilter component will be the way to remove a store filter
  const filteredAppliedFilters = ( filtersColumn?.appliedFilters || [] ).filter(
    ( filter ) => filter.groupType !== FILTER_GROUPS.AVAILABLE_STORE_IDS
  );

  let selectedGuidedNavItems = guidedNavigationModel?.items?.filter( item => item.selected ) || [];

  const filterItems = [
    ...selectedGuidedNavItems,
    ...filteredAppliedFilters
  ];

  return filterItems;
};

/**
 * filter ads as per inflection
 *
 * @param {object} data arguments
 */
export const getFilteredAds = ( data ) => {
  const { adsModule, inflection } = handleEmptyObjects( data );
  if( !adsModule || !inflection ){
    return;
  }
  const filteredAds = adsModule[inflection?.TYPE]?.moduleRefs?.map( ( i ) => {
    return adsModule.modules?.find( ( el ) => el.id === i );
  } );
  return filteredAds;
};

/**
 * Return the count of products per rows in different screen size
 *
 * @param {object} data arguments
 */
export const getProductPerRow = ( data ) => {
  const { breakpoint } = handleEmptyObjects( data );
  let productPerRow;
  switch ( breakpoint?.CURRENT_BREAKPOINT ){
    case 'XL':
    case 'LG':
      productPerRow = 4;
      break;
    case 'MD':
      productPerRow = 3;
      break;
    default:
      productPerRow = 2;
  }
  return productPerRow;
};

/**
 * To render ads as per dynamic position
 *
 * @param {object} data arguments
 */
export const getAdsRendered = ( data ) => {
  const { filteredAds, combinedArray, inflection, className } = handleEmptyObjects( data );
  const filteredAdsTemp = filteredAds && [...filteredAds];

  filteredAdsTemp?.map( ( module, index ) => {
    if( module ){
      const isDoubleWide = module?.variant === 'doubleWide' || module?.type === 'doubleWide';
      const containerClassName = isDoubleWide ? '__interrupterDoubleWideAds' : className;
      const isPositionAvailable = combinedArray.length >= module?.position;
      const isExemptModule = module.moduleName !== 'InterrupterRow' &&
        module.moduleName !== 'Interrupters' &&
        module.moduleName !== 'InterrupterCardContainer';

      if( isPositionAvailable || isExemptModule ){
        const el = (
          <li key={ `ad-${module?.position}` }
            className={ `ProductListingResults${containerClassName}` }
          >
            <AsyncComponent key={ `${inflection.TYPE}:${module.id}:${index}` }
              { ...spreadProps( { ...module } ) }
            />
          </li>
        );
        // If isDoubleWide and isPositionAvailable is true, insert a placeholder null element to
        // occupy the subsequent module position, ensuring layout continuity without disruption.
        // Otherwise, add the element at its designated position.
        if( isPositionAvailable && isDoubleWide ){
          combinedArray.splice( module?.position - 1, 0, el );
          combinedArray.splice( module?.position, 0, null );
        }
        else if( !isDoubleWide ){
          combinedArray.splice( module?.position - 1, 0, el );
        }
      }
    }
  } );
};

/**
 * Saves user filter prefrence to local storage and close overlay on mobile
 *
 * @param {object} data arguments
 * @param {boolean} data.isFilterShown isFilterShown
 * @param {boolean} data.isMobileDevice isMobileDevice
 * @param {object} methods methods
 * @param {function} methods.closeOverlay closeOverlay
 */
export const onShowFiltersChange = ( data, methods ) => {
  const { isFilterShown } = data || {};
  const { closeOverlay } = methods || {};

  if( !isFilterShown && closeOverlay ){
    closeOverlay();
  }

  const filterValue = getStorage( { secure: false, key: STORAGE_KEY.productFilterDrawerPersist } );

  if( !!filterValue?.isFilterShown === !!isFilterShown ){
    return;
  }

  setStorage( { secure: false, key: STORAGE_KEY.productFilterDrawerPersist, value: { isFilterShown } } );
};

/**
 * Handles on intersection of a child item in the list
 * @param {object} data
 */
export const composeOnCardIntersect = ( data, methods ) => () => {
  const { debounceIntersectionRef, isScrolling, loading } = data || {};
  const { debouncedIntersectionHandler } = methods || {};
  if( loading || isScrolling.current || !debouncedIntersectionHandler ){
    return;
  }
  clearTimeout( debounceIntersectionRef?.current );
  debounceIntersectionRef.current = setTimeout( debouncedIntersectionHandler, DEBOUNCE_CARD_INTERSECTION_TIMEOUT );
};

/*
 * Handles the filter state
 */
export const loadFilterState = ( methods ) => {
  const { setIsFilterShown } = methods || {};
  const filterValue = getStorage( { secure: false, key: STORAGE_KEY.productFilterDrawerPersist } );
  const navigationType = utils.getNavigationType();
  if( filterValue?.isFilterShown === true && navigationType === BROWSER_NAVIGATION_TYPE.BackForward ){
    setIsFilterShown( filterValue?.isFilterShown );
  }
};

/*
 * get the Previously scrolled Item from local storage and assign it to previouslyScrolledSku
 */
export const loadPreviousSku = ( data ) => {
  const { previouslyScrolledSku = {} } = handleEmptyObjects( data );
  const navigationType = utils.getNavigationType();
  const isBackForward = navigationType !== BROWSER_NAVIGATION_TYPE.BackForward;
  const isSafariReload = isSafari() && navigationType === BROWSER_NAVIGATION_TYPE.Reload;
  if( isBackForward && !isSafariReload ){
    return;
  }

  const prevId = getStorage( { secure: false, key: PREVIOUSLY_SCROLLED_STORAGE_KEY } )?.id;

  if( prevId ){
    previouslyScrolledSku.current = getStorage( { secure: false, key: PREVIOUSLY_SCROLLED_STORAGE_KEY } )?.id;
    removeStorage( { secure: false, key: PREVIOUSLY_SCROLLED_STORAGE_KEY } );
  }
};

/*
 * scroll to the last scrolled item.
 */
export const scrollToPreviousSku = ( data ) => {
  const { isScrolling, items, itemSelectorTemplate, loading, meta, previouslyScrolledSku } = data;

  if( items?.length <= 0 || !previouslyScrolledSku.current || loading || !!meta?.initAction?.graphql ){
    return;
  }

  setTimeout( () => {
    listenForElement(
      {
        onElementFound: ( el ) => {
          if( isScrolling.current ){
            return;
          }

          isScrolling.current = true;
          scrollToElement( { el, animate: true, offset: -100, autoFocus: false } );

          // Arbitrary timeout to prevent multiple scrolls
          setTimeout( () => {
            isScrolling.current = false;
          }, 700 );
        }
      },
      { querySelector: itemSelectorTemplate?.replace( '{id}', previouslyScrolledSku.current ) }
    );
    previouslyScrolledSku.current = false;
  }, 10 );
};

/**
 * Update the products viewed label based on the provided data and methods.
 *
 * @function
 * @param {object} data - An object containing the following properties:
 *   - productsList {Array} - A list of products
 *   - productsViewedTemplateLabel {string} - A template string for the products viewed label.
 *   - pageSize {number} - The number of products per page.
 * @param {object} methods - An object containing the following method:
 *   - setProductsViewedCountLabel {function} - A function to set the products viewed count label.
 * @return {undefined} - This function does not return anything.
 */
export const updateProductsViewedLabel = ( data, methods ) => {
  const { productsList, productsViewedTemplateLabel, pageSize } = data || {};
  const { setProductsViewedCountLabel } = methods || {};
  if( !productsList || productsList?.length === 0 ){
    return;
  }
  const maxPageObject = productsList.reduce( ( maxPageObject, current ) =>
    current.pageNumber > maxPageObject.pageNumber ? current : maxPageObject
  );
  const lastPageSize = productsList.filter( ( item ) => item.pageNumber === maxPageObject.pageNumber ).length;
  const totalViewdItemsCount = ( maxPageObject.pageNumber - 1 ) * pageSize + lastPageSize;
  setProductsViewedCountLabel( productsViewedTemplateLabel?.replace( '%1', totalViewdItemsCount ) );
};

/**
 * updateIsLoadPreviousShown
 *
 * @param {Object} data - An object containing the productsList and pageSize
 * @param {Object} methods - An object containing the setIsLoadPreviousShown method
 *
 * @return {undefined} - This function does not return anything.
 */
export const isLoadPreviousShown = ( data ) => {
  const { productsList, pageSize, loadPreviousAction } = data || {};
  if( !productsList || productsList?.length === 0 ){
    return false;
  }
  const maxPageObject = productsList.reduce( ( maxPageObject, current ) =>
    current.pageNumber > maxPageObject.pageNumber ? current : maxPageObject
  );
  const lastPageSize = productsList.filter( ( item ) => item.pageNumber === maxPageObject.pageNumber ).length;
  return (
    ( maxPageObject.pageNumber - 1 ) * pageSize + lastPageSize !== productsList.length && loadPreviousAction !== null
  );
};

/**
 * Determines if the load more button should be displayed.
 *
 * @param {Object} data An object with the following properties:
 *   - productsList: An array of products, where each product has a pageNumber property
 *   - pageSize: The number of products to display per page
 *   - resultCount: The total number of products in the list
 *   - loadMoreAction: A function to be called when the load more button is clicked
 *
 * @return {Boolean} Returns `true` if the load more button should be displayed, `false` otherwise
 */
export const isLoadMoreShown = ( data ) => {
  const { productsList, pageSize, resultCount, loadMoreAction } = data || {};
  if( !productsList || productsList?.length === 0 ){
    return false;
  }
  const maxPageObject = productsList.reduce( ( maxPageObject, current ) =>
    current.pageNumber > maxPageObject.pageNumber ? current : maxPageObject
  );
  const lastPageSize = productsList.filter( ( item ) => item.pageNumber === maxPageObject.pageNumber ).length;
  return ( maxPageObject.pageNumber - 1 ) * pageSize + lastPageSize !== resultCount && loadMoreAction !== null;
};

/**
 * @function composedPath
 * @param {Object} data - An object containing the event and itemSelectorClassName
 * @param {Event} data.event - The event object that was triggered
 * @param {string} data.itemSelectorClassName - The class name of the element to find
 *
 * @returns {HTMLElement} - The parent node that matches the specified class name
 *
 * Returns the parent node of the element that was clicked that matches the specified class name
 */
export const composedPath = ( data ) => {
  const { event, itemSelectorClassName } = handleEmptyObjects( data );
  if( !event ){
    return;
  }
  let currentNode = event.target.parentNode;
  while ( currentNode !== document.body ){
    if( currentNode.classList.contains( itemSelectorClassName ) ){
      // found the correct parent node
      break;
    }
    currentNode = currentNode.parentNode;
  }
  return currentNode;
};

/**
 * @function handleProductClick
 * @param {Event} event - The event object that was triggered
 * @param {string} itemSelectorClassName - The class name of the element that was clicked
 * @param {string} itemSelectorAttribute - The attribute of the element that was clicked
 * @param {Object} itemsDict - The dictionary object containing the item's information
 * @param {function} handleReplace - A function that handles the page url
 * @param {function} setStorage - A function that sets a value in storage
 * @param {string} PREVIOUSLY_SCROLLED_STORAGE_KEY - The key to use when setting value in storage
 *
 * Handle the click event on a product element
 * and sets the ID of the product in storage
 */
export const handleProductClick = ( data, methods ) => {
  const {
    event,
    itemSelectorClassName,
    itemSelectorAttribute,
    itemsDict,
    PREVIOUSLY_SCROLLED_STORAGE_KEY,
    previewDate
  } = data || {};
  const { handleReplace, setStorage } = methods || {};
  const child = utils.composedPath( { event, itemSelectorClassName } );
  const skuId = child?.getAttribute( itemSelectorAttribute );
  if( skuId ){
    handleReplace( { url: itemsDict.current[skuId]?.pageUrl, previewDate, preserveParams: true } );
    setStorage( { secure: false, key: PREVIOUSLY_SCROLLED_STORAGE_KEY, value: { id: skuId } } );
  }
};

/**
 * @const {string} ULTA_PREVIOUSLY_SCROLLED_PRODUCT - Previously scrolled to item's local storage key
 */
export const PREVIOUSLY_SCROLLED_STORAGE_KEY = 'ULTA_PREVIOUSLY_SCROLLED_PRODUCT';

/**
 * @const {number} DEBOUNCE_CARD_INTERSECTION_TIMEOUT - Debounce value for batching product card data capture intrestion events
 */
export const DEBOUNCE_CARD_INTERSECTION_TIMEOUT = 300;

/**
 * Create the array for visible items
 * @param {object} data
 * @param {object} methods
 */
export const composeOnCardDataCapture = ( data ) => ( id ) => {
  const { visibleItems, loading } = data || {};
  if( loading || !Array.isArray( visibleItems?.current ) ){
    return;
  }
  visibleItems.current = [...visibleItems.current, id];
};

/**
 * Create the list of item aaray with item id
 * @param {object} data
 * @returns item list
 */
export const getItemList = ( data ) => {
  const { loadMoreTemplateUrl, itemIndexAttribute, productsList = [] } = handleEmptyObjects( data );
  return productsList.reduce( ( acc, item, index ) => {
    const pageUrl =
      item.pageNumber > 1 ?
        loadMoreTemplateUrl?.replace( '%1', item.pageNumber ) :
        loadMoreTemplateUrl?.replace( /[?&]page=%1/g, '' );
    return {
      ...acc,
      [item[itemIndexAttribute] ? item[itemIndexAttribute] : index]: {
        ...item,
        pageUrl
      }
    };
  }, {} );
};

/**
 * Handle datacapture data
 * @param {object} data
 * @param {object} methods
 * @returns timer
 */
export const setupDCTimer = ( data, methods ) => {
  const interval = 500;
  return global.setInterval( () => handleQueuedDCItems( data, methods ), interval );
};

/**
 * Aggregate datacapture data
 * @param {object} data
 * @param {{ current: array }} data.visibleItems Ref that stores current queue of visible items
 * @param {{ current: array }} data.eventTriggeredItemList Ref that stores already processed events
 * @param {boolean} data.loading Is loading
 * @param {object} methods
 */
export const handleQueuedDCItems = ( data ) => {
  const { visibleItems, itemsDict, eventTriggeredItemList } = handleEmptyObjects( data );

  if( !visibleItems || visibleItems.current.length === 0 ){
    return;
  }

  let events = {};

  for ( const skuId of visibleItems.current ){
    const item = itemsDict.current[skuId];
    if( !item?.dataCapture || eventTriggeredItemList.current.includes( skuId ) ){
      continue;
    }

    events = merge( events, item.dataCapture );
    eventTriggeredItemList.current = [...eventTriggeredItemList.current, skuId];
  }

  if( events?.dataLayer?.Tealium ){
    events.dataLayer.Tealium = utils.removeTealiumDuplicates( events.dataLayer.Tealium );
  }

  triggerScrollDCEvent( events );

  visibleItems.current = [];
};

/** Remove Duplicates method exported for testing
 * @param {object} events - DCEvent
 */
export const removeTealiumDuplicates = ( tealium ) => {
  const sanitizedTealium = removeTargetDuplicates( tealium, ['filter_label', 'filter_group'] );
  return sanitizedTealium;
};

/**
 * @param {object} data - passing previousAction and items
 * @param {object} methods - passing methods for update items
 */
export const updateProductLists = ( data, methods ) => {
  const { items, previousAction, page, guidedNavigationModel } = data || {};

  if( !previousAction ){
    return;
  }

  let selectedGuidedNavItems = guidedNavigationModel?.items?.filter( item => item.selected ) || [];

  let updatedItems = items.map( ( item ) => {
    return { ...item, pageNumber: page };
  } );

  if( previousAction.current === FILTER_EVENT.LOAD_MORE ){
    if( selectedGuidedNavItems.length > 0 ){
      methods.setProductsList( updatedItems );
    }
    else {
      methods.setProductsList( ( prevProductList ) => [...prevProductList, ...updatedItems] );
    }
  }
  else if( previousAction.current === FILTER_EVENT.LOAD_PREVIOUS ){
    methods.setProductsList( ( prevProductList ) => [...updatedItems, ...prevProductList] );
  }
  else {
    methods.setProductsList( updatedItems );
  }
};

/**
 * Updates the interrupter rows and dynamic ads lists based on the previous action, guided navigation state, and applied filters.
 *
 * @param {object} data - Contains the data required for processing updates.
 * @param {object} data.props - The properties containing `interrupterRows` and `dynamicAds` data.
 * @param {object} data.previousAction - The current filter action (e.g., `LOAD_MORE`, `LOAD_PREVIOUS`).
 * @param {object} [data.guidedNavigationModel] - The guided navigation model containing selectable items.
 * @param {Array} [data.appliedFilters] - An array of applied filters.  Used to determine if any filters are active.
 * @param {object} methods - Contains methods to update the interrupter rows and dynamic ads.
 * @param {function} methods.setInterrupterRowsList - Method to update the list of interrupter rows.
 * @param {function} methods.setDynamicAdsList - Method to update the list of dynamic ads.
 */
export const updateInterrupters = ( data, methods ) => {
  const { props, previousAction, guidedNavigationModel, appliedFilters } = handleEmptyObjects( data );
  const { setInterrupterRowsList, setDynamicAdsList } = handleEmptyObjects( methods );

  if( !setInterrupterRowsList || !setDynamicAdsList || !previousAction || !props ){
    return;
  }

  const selectedGuidedNavItems = guidedNavigationModel?.items?.filter( item => item.selected ) || [];
  const noFiltersSelected = selectedGuidedNavItems?.length === 0 && appliedFilters?.length === 0;

  if( previousAction.current === FILTER_EVENT.LOAD_MORE && noFiltersSelected ){
    setInterrupterRowsList( props?.interrupterRows );
    setDynamicAdsList( prevDynamicAds => merge( prevDynamicAds ?? {}, props?.dynamicAds ?? {} ) );
  }
  else if( previousAction.current === FILTER_EVENT.LOAD_PREVIOUS && noFiltersSelected ){
    setInterrupterRowsList( props?.interrupterRows );
    setDynamicAdsList( prevDynamicAds => merge( props?.dynamicAds ?? {}, prevDynamicAds ?? {} ) );
  }
  else {
    setInterrupterRowsList( props?.interrupterRows );
    setDynamicAdsList( props?.dynamicAds );
  }
};

/**
 * Close filters when not on mobile
 * @param {object} methods - methods
 * @param {function} data.closeFiltersOverlay - Closes overlay
 * @param {object} data - params
 * @param {boolean} data.isMobileDevice - Is inflection at mobile breakpoints
 * @param {object} data.overlayFlyout - Dialog DOM element
 */
export const closeFiltersOverlayOnDesktop = ( data, methods ) => {
  if( !data?.isMobileDevice ){
    methods?.closeOverlay?.();
  }
};

/**
 * Opens and overlay with mobile filters inside
 * @param {object} methods - Callbacks
 * @param {function} methods.openOverlay - openOverlay callback
 * @param {function} methods.onClose - openOverlay on close
 * @param {object} data - Data needed for overlay
 * @param {boolean} data.filtersEnabled - if filters are enabled (available)
 * @param {boolean} data.isFilterShown - Should show filter column
 * @param {boolean} data.closeAction - closeAction for closing overlay to handle data capture
 * @param {object} data.opener - Ref for open button
 * @param {object} data.content - Filter column React element
 * @param {object} methods.setIsFilterShown - set filter shown state on overlay
 */
export const openFiltersOverlay = ( data, methods ) => {
  const { isFilterShown, content, opener, filtersEnabled, closeAction } = handleEmptyObjects( data );
  const { openOverlay, setIsFilterShown } = methods || {};

  if( !filtersEnabled || !isFilterShown || !content || !openOverlay ){
    return;
  }

  openOverlay( {
    onClose: () => setIsFilterShown( false ),
    type: DXL_NAVIGATION_TYPE.Flyout,
    alignment: 'right',
    crossButtonVisibility: true,
    opener,
    content,
    closeAction
  } );
};

/**
 * @method isEmptyProductsList
 * @summary Method detecting whether an argument is a not empty array
 * @param {array} productsList - array of ProductCard properties
 * @returns {boolean}
 */
export const isEmptyProductsList = ( productsList ) => !Array.isArray( productsList ) || productsList.length === 0;

/**
 * @method isEmptyResultsLabelShown
 * @summary Method detecting the empty results markdown rendering conditions
 * @param {array} productsList - array of ProductCard properties
 * @param {boolean} isMobileDevice - is mobile device boolean flag
 * @param {boolean} isFilterShown - a flag describing whether a filter column is opened
 * @returns {boolean}
 */
export const isEmptyResultsLabelShown = ( { productsList, isMobileDevice, isFilterShown } ) => {
  const isProductListEmpty = isEmptyProductsList( productsList );

  if( isMobileDevice ){
    return isProductListEmpty && !isFilterShown;
  }

  return isProductListEmpty;
};

/**
 * @method isFilterShownOnMobile
 * @summary Method detecting the filters column rendering conditions on mobile devices
 * @param {boolean} isMobileDevice - is mobile device boolean flag
 * @param {boolean} isFilterShown - a flag describing whether a filter column is opened
 * @returns {boolean}
 */
export const isFilterShownOnMobile = ( { isMobileDevice, isFilterShown } ) => isMobileDevice && isFilterShown;

/**
 * @method onLoadClick
 * @summary Method dispatch action onClick of load more button and fetch the updated ProductListingWrapper
 * @param {object} data - defines the action type
 * @param {object} methods -dispatch function with specific type
 */
export const onLoadClick = ( data, methods ) => {
  const { loadAction, id, event } = data || {};
  const { setFilterRequest, setShowGhostCards } = methods || {};

  if( !setFilterRequest || !setShowGhostCards ){
    return;
  }

  setFilterRequest( {
    id,
    action: loadAction,
    event
  } );

  setShowGhostCards( true );
};

/**
 * @method onSort
 * @summary Method dispatch action onChange of filter dropdown and fetch the updated ProductListingWrapper
 * @param {object} event - target element object
 * @param {string} type - defines the action type
 * @param {function} setFilterRequest - dispatch DXL action
 */
export const onSort = ( event, methods ) => {
  const { action, id, applySortUrl: clientUrl } = event?.data || {};

  methods?.setFilterRequest?.( {
    id,
    action,
    clientUrl,
    event: FILTER_EVENT.SORT
  } );
};

/**
 * @method getNavigationType
 * @summary Method to get the browser naviagtion type
 */
export const getNavigationType = () => {
  const navigation = global.performance?.getEntriesByType?.( 'navigation' )?.[0];

  if( navigation ){
    return navigation.type;
  }
  return null;
};

/**
 * @method safariReload
 * @summary Method to reload safari
 */
export const safariReload = ( event ) => {
  if( !event.persisted || !isSafari() ){
    return;
  }
  handleReload();

};

export const BROWSER_NAVIGATION_TYPE = {
  BackForward: 'back_forward',
  Reload: 'reload',
  Navigate: 'navigate',
  Prerender: 'prerender'
};

/**
 * Transforms the skip redirect data by splitting the skip redirect label and interpolating the URL.
 *
 * @param {Object} data - The data containing the skip redirect label, action, and search term.
 * @param {string} data.skipRedirectLabel - The label containing '%1' as a placeholder to be split.
 * @param {Object} data.skipRedirectAction - The action containing the URL for redirection.
 * @param {string} data.skipRedirectAction.url - The URL that may contain placeholders to be replaced.
 * @param {string} [data.redirectedSearchTerm] - The search term used to interpolate into the URL.
 *
 * @returns {Object|undefined} The transformed skip redirect data, including the first and second parts of the label, and the interpolated URL.
 * @returns {string} return.transformedSkipRedirectPart1 - The first part of the transformed skip redirect label.
 * @returns {string} return.transformedSkipRedirectPart2 - The second part of the transformed skip redirect label.
 * @returns {string} return.transformedSkipRedirectUrl - The interpolated skip redirect URL.
 */
export const transformSkipRedirect = ( data ) => {
  const { skipRedirectLabel, skipRedirectAction, redirectedSearchTerm } = data || {};

  if( !skipRedirectLabel || !skipRedirectAction?.url ){
    return;
  }

  const transformedSkipRedirectLabel = skipRedirectLabel.split( '%1' );
  const transformedSkipRedirectPart1 = transformedSkipRedirectLabel[0];
  const transformedSkipRedirectPart2 = transformedSkipRedirectLabel[1];
  const transformedSkipRedirectUrl = interpolateString( skipRedirectAction.url, [encodeURIComponent( redirectedSearchTerm )] );

  return {
    transformedSkipRedirectPart1,
    transformedSkipRedirectPart2,
    transformedSkipRedirectUrl
  };
};

/**
 * Represents a SearchResultsHeader component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns SearchResultsHeader
 */

export const SearchResultsHeader = function( props ){
  const { transformedSkipRedirectUrl,
    transformedSkipRedirectPart1,
    transformedSkipRedirectPart2
  } = utils.transformSkipRedirect( props ) || {};

  const isRedirectVisible = !!props?.isRedirect && props?.skipRedirectAction?.url && props?.skipRedirectLabel;

  return (
    <div
      className={ classNames( 'ProductListingWrapper__resultslabel', {
        ProductListingWrapper__resultslabel__isSearchResult: props.search || props.isMobileDevice
      } ) }
      id='product-listing-wrapper-resultslabel'
      aria-live='polite'
      { ...spreadProps( { 'aria-busy': props.ariaBusy } ) }
    >
      { props.headerStyle && (
        <div
          className={ classNames( {
            [props.headerStyle.className]: props.headerStyle.className
          } ) }
        >
          <Text
            htmlTag={ props.htmlTag }
            { ...spreadProps( {
              textStyle: props.headerStyle.textStyle,
              color: props.headerStyle.color
            } ) }
          >
            { props.resultCountLabel }
          </Text>
        </div>
      ) }
      { !props?.isRedirect && (
        <Text htmlTag='span'
          textStyle='body-2'
        >
          { props.noResultsLabel }
        </Text>
      ) }
      { !!props.skipAutoCorrectAction?.url && (
        <>
          { ' ' }
          &quot;
          <Link_Huge
            url={ props.skipAutoCorrectAction.url }
            action={ props.skipAutoCorrectAction }
            onClick={ () => {
              handleRedirect( { url: props.skipAutoCorrectAction.url } );
            } }
          >
            <Text htmlTag='span'
              textStyle='body-2'
              color='neutral-700'
            >
              { props.skipAutoCorrectAction.label }
            </Text>
          </Link_Huge>
          &quot;
        </>
      ) }
      { isRedirectVisible && (
        <>
          <Text htmlTag='span'
            textStyle='body-2'
          >
            { transformedSkipRedirectPart1 }
            <Text
              htmlTag='span'
              textStyle='body-2'
              fontWeight='bold'
            >{ props.pageTitle }</Text>
            { transformedSkipRedirectPart2 }
          </Text>
          <>
            { ' ' }
            &quot;
            <Link_Huge
              url={ transformedSkipRedirectUrl }
              action={ props.skipRedirectAction }
              onClick={ () => {
                handleRedirect( { url: transformedSkipRedirectUrl } );
              } }
            >
              <Text htmlTag='span'
                textStyle='body-2'
                color='neutral-700'
              >
                { props.redirectedSearchTerm }
              </Text>
            </Link_Huge>
            &quot;.
          </>
        </>
      ) }
    </div>
  );
};

/**
 * Represents a helper method to manage state for initial state for Ghost Loader
 *
 * @param {object} data - defines the action type
 * @param {object} methods - dispatch function with specific type
 */
export const manageLoadingState = ( data, methods ) => {
  const { loading } = handleEmptyObjects( data );
  const { setShowGhostCards } = methods || {};

  // We need to granularly manage the loading state
  if( loading || !setShowGhostCards ){
    return;
  }

  setShowGhostCards( false );
};
/**
 * Property type definitions
 *
 * @typedef ProductListingWrapperProps
 * @type {object}
 * @property {object} items - Sets the items of the page
 * @property {boolean} loading - Flag to render the data or not
 * @property {object} filtersColumn - Sets the filters column for the page
 * @property {boolean} filtersEnabled - Determines if the filters are enabled/available
 * @property {string} resultCountLabel - Sets the result count label for the page
 * @property {string} noResultsLabel - Sets the no result label for the page
 * @property {object} skipAutoCorrectAction - Sets the skipAutoCorrectAction for the page
 * @property {object} filtersResultAction - Sets the filtersResultAction for the page
 * @property {object} loadMoreAction - Sets the loadMoreAction for the page
 * @property {object} loadPreviousAction - Sets the loadPreviousAction for the page
 * @property {string} loadMoreUrl - Sets the load more client side url
 * @property {string} loadPreviousUrl - Sets the load previous client side url
 * @property {string} productsViewedLabel - Sets the productsViewedLabel for the page
 * @property {string} noFilteredResultsLabel - Sets the noFilteredResultsLabel for the page
 * @property {object} resetFiltersAction - Sets the resultFIltersAction for the page
 * @property {string} currentUrl - Sets the url for after a filter action
 * @property {string} search - set value of search category
 * @property {object} gamBanner - set data for gamBanner
 * @property {array} messages - Sets the messages array
 */
export const propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  filtersColumn: PropTypes.object,
  filtersEnabled: PropTypes.bool,
  resultCountLabel: PropTypes.string,
  noResultsLabel: PropTypes.string,
  skipAutoCorrectAction: PropTypes.object,
  filtersResultAction: PropTypes.object,
  loadMoreAction: PropTypes.object,
  loadPreviousAction: PropTypes.object,
  loadMoreUrl: PropTypes.string,
  loadPreviousUrl: PropTypes.string,
  productsViewedLabel: PropTypes.string,
  noFilteredResultsLabel: PropTypes.string,
  resetFiltersAction: PropTypes.object,
  currentUrl: PropTypes.string,
  search: PropTypes.string,
  gamBanner: PropTypes.object,
  /** Sets the messages array */
  messages: PropTypes.array
};
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS,
  filtersEnabled: true
};

ProductListingWrapper.propTypes = propTypes;
ProductListingWrapper.defaultProps = defaultProps;

export default ProductListingWrapper;